import { Breadcrumbs } from '@warp-ds/react/components/breadcrumbs';
import { useRouter } from 'next/router';
import { useCallback, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { useConfig } from 'shared/config/ConfigContext';
import { SortDetails } from 'shared/page/result/SortDetails';
import type { DeviceType } from 'shared/types';
import type { FhhState, SearchId } from '@/domain';
import { useExperiment } from '@/hooks/experiments';
import type { LoginId } from '@/types/common';
import { EventBanner } from './components/event/EventBanner';
import { Filter } from './components/filters/Filter';
import { FhhSearch } from './components/newSearch/FhhSearch';
import { ResultsList } from './components/ResultsList';
import { ResultsToolbar } from './components/ResultsToolbar';
import { useFhhSearch } from './hooks/useFhhSearch';
import { useFilterResults } from './hooks/useFilterResults';
import { useInitialBannerFilters } from './hooks/useInitialBannerFilters';
import { useInitialFilterSync } from './hooks/useInitialFilterSync';
import { useResultPageTracking } from './hooks/useResultPageTracking';

const EXPERIMENT_BREACH_DETECTION = 'tjvr-breach-detection-in-boost';
const EXPERIMENT_VARIANT_TREATMENT = 'treatment';

export interface ResultPageProps {
    deviceType: DeviceType;
    loginId?: LoginId;
    searchId: SearchId;
}

export const ResultPage = ({ deviceType, loginId, searchId }: ResultPageProps) => {
    const experiment = useExperiment();
    const useNewDefaultSorting = experiment?.[EXPERIMENT_BREACH_DETECTION] === EXPERIMENT_VARIANT_TREATMENT;
    const { apiExternalUrl } = useConfig();
    const router = useRouter();
    const { filters, filterData, autocompleteData } = useSelector((state: FhhState) => state.results, shallowEqual);
    const { data, error, loading } = useFhhSearch({
        searchId,
        effectiveFilters: { ...filters, ...router.query, useNewDefaultSorting },
    });

    useInitialFilterSync(router.query, deviceType);
    useInitialBannerFilters(filters, autocompleteData, router.query);
    useResultPageTracking(filters, data, router);

    const [showFilters, setShowFilters] = useState(false);

    const filterResults = useFilterResults(apiExternalUrl, useNewDefaultSorting);
    const toggleFilters = useCallback(() => setShowFilters((prev) => !prev), []);

    return (
        <>
            <Breadcrumbs className="sr-only md:not-sr-only pt-16! my-16!">
                <a href="/reise/">Reise</a>
                <a href="/reise/feriehus-hytteutleie/" data-testid="submarket">
                    Feriehjem og hytter
                </a>
                <span aria-current="page">Resultat</span>
            </Breadcrumbs>
            <h1 className="sr-only">Feriehjem og hytter</h1>
            {filters.event && <EventBanner event={filters.event} />}
            <FhhSearch filters={filters} />
            <div className="md:grid md:grid-cols-3 md:gap-32 pb-32" data-testid="resultPage">
                <Filter
                    onFilterChanged={filterResults}
                    data={filterData}
                    filters={filters}
                    showMobileModal={showFilters}
                    onDismiss={toggleFilters}
                />
                <div className="md:col-span-2 flex flex-col md:items-center">
                    <ResultsToolbar
                        showFilters={showFilters}
                        toggleFilters={toggleFilters}
                        filters={filters}
                        autocompleteData={autocompleteData}
                        loginId={loginId}
                        onFilterChanged={filterResults}
                    />
                    <SortDetails section="feriehus" className="flex mb-16 w-fit md:justify-center md:-mt-[52px] md:mb-32" />
                    <ResultsList
                        data={data}
                        loading={loading}
                        error={error}
                        filters={filters}
                        loginId={loginId}
                        onFilterChanged={filterResults}
                    />
                </div>
            </div>
        </>
    );
};
